/**
 * When true: no requests leave the app to your backend (all ApiCall* are stubbed),
 * and SocketContext / P2P / mobile chart sockets do not connect.
 * Third-party chart calls (e.g. Binance) are also skipped where wired below.
 * Set to false for normal login/logout against your API.
 */
export const DISABLE_ALL_NETWORK = false;

/**
 * Same-origin API + sockets. Browser calls `/v1/...` on the frontend host
 * (e.g. localhost:9502). webpack-dev-server (`src/setupProxy.js`) forwards
 * to the real backend. Do not put the backend host here or it appears in DevTools.
 */
const apiOrigin = "";
const appUrl = apiOrigin;
const socketUrl =
  typeof window !== "undefined" && window.location && window.location.origin
    ? window.location.origin
    : "";

/**
 * Public media host (S3 or CloudFront). Must match backend S3_PUBLIC_BASE_URL.
 * When API already returns absolute https URLs, FE uses them as-is:
 *   <img src={icon_path} />
 */
const s3PublicBaseUrl = String(
  process.env.REACT_APP_S3_PUBLIC_BASE_URL
  || "https://agcx-data-storage-s3-uae.s3.me-central-1.amazonaws.com"
).replace(/\/$/, "");

const LEGACY_AVATAR_HOSTS = [
  "https://backend.arabglobal.ae",
  "http://backend.arabglobal.ae",
  "http://localhost:9503",
  "https://localhost:9503",
];

/**
 * Normalize any stored media value to an absolute URL for <img src={…} />.
 * Prefer API responses that already return https://… (S3/CDN).
 */
export function resolvePublicUploadUrl(path) {
  if (path == null) return "";
  const t = String(path).trim();
  if (!t) return "";

  if (/^https?:\/\//i.test(t) || t.startsWith("data:") || t.startsWith("blob:")) {
    return t;
  }

  if (t.startsWith("/images/") || t.startsWith("images/")) {
    return t.startsWith("/") ? t : `/${t}`;
  }

  const normalized = t.replace(/^\/+/, "").replace(/\\/g, "/");

  // S3 object keys (versioned public + stable static)
  if (
    normalized.startsWith("public/")
    || normalized.startsWith("static/")
  ) {
    const key = normalized.startsWith("static/") ? `public/${normalized}` : normalized;
    return `${s3PublicBaseUrl}/${key}`;
  }

  // Private keys — caller should use signed download-url; do not invent a public URL
  if (normalized.startsWith("user/") || normalized.startsWith("admin/")) {
    return "";
  }

  // Legacy disk paths under API (uploads/, icons/btc.png, apk/…)
  const base = String(appUrl).replace(/\/$/, "");
  return `${base}/${normalized}`;
}

/** Alias — same as resolvePublicUploadUrl (absolute URL for img src). */
export function toAbsoluteMediaUrl(path) {
  return resolvePublicUploadUrl(path);
}

/**
 * Avatar display URL. Absolute S3/CDN URLs pass through.
 * Legacy API-host absolute URLs are rewritten to current apiOrigin.
 */
export function resolveUserAvatarUrl(path) {
  if (path == null) return "";
  let t = String(path).trim();
  if (!t) return "";
  if (t.startsWith("data:") || t.startsWith("blob:")) return t;

  if (/^https?:\/\//i.test(t)) {
    // Already absolute (S3, CDN, or API) — only rewrite known legacy hosts
    const normalizedOrigin = String(apiOrigin).replace(/\/$/, "");
    for (const host of LEGACY_AVATAR_HOSTS) {
      if (t.startsWith(`${host}/`)) {
        const rest = t.slice(host.length).replace(/^\/+/, "");
        // If path is an S3 key under legacy host, prefer S3 base
        if (rest.startsWith("public/") || rest.startsWith("static/")) {
          return resolvePublicUploadUrl(rest);
        }
        return `${normalizedOrigin}/${rest}`;
      }
    }
    return t;
  }

  return resolvePublicUploadUrl(t) || (() => {
    if (t.startsWith("/images/") || t.startsWith("images/")) {
      return t.startsWith("/") ? t : `/${t}`;
    }
    const base = String(apiOrigin).replace(/\/$/, "");
    return `${base}/${t.replace(/^\/+/, "")}`;
  })();
}

export const deployedUrl = `${window.origin}/`;

export const ApiConfig = {
  // =========EndPoints==========
  loginwithGoogle: 'third-party-login',
  signupwithGoogle: 'third-party-signup',
  login: "login",
  registerEmail: "register-email",
  registerPhone: "register-phone",
  /** Pre-check identifier — always POST /v1/user/check-signup-email (signup + login). */
  checkSignupEmail: "check-signup-email",
  /** Public waitlist registration endpoint: POST /v1/wait-list. */
  joinWaitlist: "wait-list",
  registrationOtp: "registration-otp",
  verifyRegistrationToken: "verify-registration-token",
  verifyRegistrationOtp: "verify-registration-otp",
  getOtp: "send-otp",
  getcode: "verify-otp",
  forgotpassword: "forgot_password",
  /** Lost Login 2-Step method — POST /v1/user/login-methods/recovery/start (tempToken, no Bearer) */
  loginMethodRecoveryStart: "login-methods/recovery/start",
  /** Prove remaining contacts — POST /v1/user/login-methods/recovery/verify (tempToken, no Bearer) */
  loginMethodRecoveryVerify: "login-methods/recovery/verify",
  /** Zand AED fiat — `GET/POST /v1/fiat/virtual-accounts` */
  fiatVirtualAccountMe: "virtual-accounts/me",
  fiatVirtualAccounts: "virtual-accounts",
  fiatLimits: "limits",
  fiatDeposits: "deposits",
  fiatDepositIntents: "deposit-intents",
  fiatConvertAssets: "convert/assets",
  fiatConvertWallets: "convert/wallets",
  fiatConvertRates: "convert/rates",
  fiatConvertQuotes: "convert/quotes",
  fiatConvertTrades: "convert/trades",
  /** `GET/POST /v1/fiat/whitelist` — bank IBANs for AED payout (Mongo: fiat_beneficiaries) */
  fiatWhitelist: "whitelist",
  /** `POST /v1/fiat/withdrawals` — AED payout to a verified whitelist entry */
  fiatWithdrawals: "withdrawals",
  fiatWithdrawalsPreview: "withdrawals/preview",
  activityLogs: "get_logs",
  setCurrency: "currency-preference",
  userfunds: "user-wallet",
  userP2pWallet: "p2p-wallet",
  walletHistory: 'wallet-history',
  depositHistory: 'wallet-deposit-history',
  /** `GET /v1/wallet/deposits?limit=50` */
  walletDeposits: "deposits",
  /** `GET/POST /v1/wallet/deposit-declarations` and `GET/POST /v1/wallet/deposit-declarations/:depositId` */
  depositDeclarations: "deposit-declarations",
  withdrawalHistory: 'wallet-withdrawal-history',
  /** `POST /v1/wallet/get-and-generate-address` — body `{ tokenAssetId, short_name, generate }` */
  getAndGenerateAddress: "get-and-generate-address",
  /** Legacy: `PUT /v1/wallet/generate-address` — body `{ generate, chain, short_name?, token_asset_id? }` (kept for back-compat). */
  generateAddress: "generate-address",
  estimatedPortfolio: "estimated-portfolio",
  allWalletsPortfolio: "all-wallets-portfolio",
  withdrawalcurrencypath: 'withdrawal',
  /** `POST /v1/wallet/verify-withdrawal` — typically server/webhook; optional client use */
  verifyWithdrawal: 'verify-withdrawal',
  /** `/v1/setting/*` — nickname & avatar (Bearer) */
  settingGetNickname: "get-nickname",
  settingEditNickname: "edit-nickname",
  settingGetAvatar: "get-avatar",
  settingChangeAvatar: "change-avatar",
  settingGetNotificationSettings: "get-notification-settings",
  settingUpdateNotificationSettings: "update-notification-settings",
  settingGetAlertSettings: "get-alert-settings",
  settingUpdateAlertSettings: "update-alert-settings",

  /** `GET /v1/wallet/deposit-coins` — Bearer (current deposit coin catalog) */
  getDepositCoins: 'deposit-coins',
  /** `GET /v1/wallet/fireblocks-deposit-coins` — Bearer (Fireblocks-backed catalog) */
  fireblocksDepositCoins: 'fireblocks-deposit-coins',
  /** `GET /v1/wallet/fireblocks-deposit-networks?symbol=USDT` — Bearer */
  fireblocksDepositNetworks: 'fireblocks-deposit-networks',
  /** `GET /v1/wallet/fireblocks-deposit-chain-types?symbol=USDT` — Bearer */
  fireblocksDepositChainTypes: 'fireblocks-deposit-chain-types',
  identity: "submit-kyc",
  /** Corporate KYB — `POST /v1/user/submit-kyb` (multipart/form-data, Bearer). */
  submitKyb: "submit-kyb",
  /** Legacy user KYC (GET) — same base as `v1/user/` */
  userKycStatusPath: "kyc-status",
  /** `GET /v1/user/vip` — Bearer. Spot, isolated, and cross share this status. */
  userVip: "vip",
  userSubmitKycGetPath: "submit-kyc",
  favoriteCoin: 'favorite-coin',
  favoriteList: 'favorite-list',
  setSecurity: "change_password",
  updateSettings: "edit-profile",
  placeOrder: 'orders',
  pastOrder: 'past-order',
  cancelOrder: 'orders',
  coinDetails: 'coin-details',
  update2fa: "enable-2fa",
  googleAuth: "generate-google-qr",
  getHistoricalData: 'historical-data',
  getDetails: "profile",
  depositInr: 'deposit_inr',
  withdrawalInr: 'withdraw_inr',
  userBankDetails: 'get_user_bank_details',
  editBank: 'user_bank_details',
  sendkginotp: 'send-kgin-otp',
  verifykginotp: 'verify-kgin-otp',
  notifiactionlist: 'user-notifications',
  reffercode: 'user_refer_code',
  getPairs: 'get-pairs',
  getMemePairs: 'get-meme-pairs',
  getHotPairs: 'get-hot-pairs',
  convertToken: "v1/swapping/convertToken",
  swapToken: "v1/swapping/swapToken",
  coinListedDetails: "send-coin-listing-request",
  partnerships: "signUp",
  total_refer_count: "total_refer_count",
  refer_list: "referral_user_list",
  referral_commission_history: "referral-commission/history",
  referral_balance: "referral_balance",
  joining_balance: "joining_balance",
  coin_details: "coin-details",
  get_top_GL: "get_top_GL",
  get_trending: "get_trending",
  user_cancel_withdrawal: "user_cancel_withdrawal",
  cancel_all_order: "cancel_all_order",
  verify_deposit: "verify-deposit",
  transfer_funds: "transfer-funds",
  p2pCoinList: "p2p_coin_list",
  fiatCurrencyList: "fiat_currency_list",
  addBankDetails: "add-bank-details",
  addUpiDetails: "add_user_upi",
  createNewPost: "create_new_post",
  sellOrder: "sell_order",
  buyOrder: "buy_order",
  fetchPaymentMethods: "fetch_payment_methods",
  getCoinList: "get-coin",
  currentPrice: "current_price",
  myAds: "my_ads",
  buyCurrency: "buy_currency",
  myOrders: "my_orders",
  p2pRuntime: "runtime_api",
  buyRequest: "buy_request",
  notifySeller: "merchant_confirmation",
  notifyMerchent: "trader_confirmation",
  addOrderNotification: "add_order_notification",
  notify: "notify_trader",
  editUpiDetails: 'edit_user_upi',
  editBankDetails: 'edit-bank-details',
  rateOrder: "rate_user",
  transferFunds: "swaping_wallets",
  submitTicket: "submit-ticket",
  getUserTickets: "get-user-tickets",
  getTicketCategories: "get-categories",
  replyTicket: "user-reply-ticket",
  swapHistory: "swapping_history",
  getLatestNews: "get_latest_news",
  quick_buy_sell: "quick_buy_sell",
  quickBuySellHistory: "history",
  request_refund: "request_refund",
  getPartnerName: "getPartnerName",
  getCvbotUserWallet: "get-user-wallet",
  markasAllRead: "mark-all-as-read",
  markasRead: "mark-as-read",
  depositCvbotFunds: "deposit-balance",
  accessTokenSumsub: "access-token-sumsub",
  editavatar: "edit-avatar",
  editusername: "edit-name",
  editemail: "edit-email",
  editPhone: "edit-phone",
  depositActiveCoins: "deposit-active-coins",
  availableWalletTypes: "available-wallet-types",
  walletTransfer: "wallet-transfer",
  walletTransferUnified: "transfer",
  transferBalance: "transfer-balance",
  getPerticularWalletBalance: "get-perticular-wallet-balance",
  getWalletBalance: "get-wallet-balance",
  walletTransferHistory: "wallet-transfer-history",
  bonusHistory: "bonus-history",
  withdrawActiveCoins: "withdraw-active-coins",
  allCoins: "all-coins",
  blogList: "blog-list",
  viewBlog: "view-blog",
  /** Public blogs — `GET /v1/blogs`, `GET /v1/blogs/:slug` (no auth) */
  publicBlogs: "blogs",
  /** Public blog filters — `GET /v1/blog-categories` (no auth) */
  blogCategories: "blog-categories",
  baseCurrencyList: "base_currency_list",
  getConversionRate: "get_conversion_rate",
  getApk: "getApk",
  platformStatus: "api/platform/status",




  // Staking Router
  stakingPackages: "packages",
  stakingPackagesForCoin: "getStakingPackagesForSingleCoin",
  stakingUserBalance: "user-balance",
  stakingRedeem: "redeem",
  stakingTopUp: "top-up",
  stakingCancelPosition: "position",
  stakingPayoutHistory: "payout-history",

  // Earning Router
  packageList: "package-list",
  subscribedPackageList: "subscribed-package-list",
  earningPortfolio: "earning-portfolio",
  earningPortfolioSummary: "earning-portfolio-summary",
  subscribeEarningPackage: "subscribe-earning-package",


  // Arbitrage Router
  getPackageList: "bot-package-listing",
  buyArbitrageBot: "buy-arbitrage-bot",
  addFunds: "add-funds",
  getActivePackage: "get-active-package",
  getInvestment: "get-investment",


  // Futures Router
  order: "order",
  cancel: "cancel",
  closePosition: "close-position",
  futuresOrders: "orders",
  futuresPositions: "positions",
  futuresExecutions: "executions",
  futuresWalletHistory: "wallet-history",

  // annoucemnt Router

  getAnnouncementCategoryList: "announcement-category-list",
  getAnnouncementList: "announcement-listing",
  announcementView: "announcement-view",


  // Launchpad Router (base: /v1/user)
  launchpadListing: "launchpad-listing",
  userLaunchpadListing: "user-launchpad-listing",
  launchpadDetails: "launchpad-details",
  userLaunchpadDetails: "user-launchpad-details",
  purchaseToken: "purchase-token",
  userPurchasingListing: "user-purchaseing-listing",
  userTokenSubscriptionHistory: "user-token-subscription-history",
  userTokenTransactionHistory: "user-token-transaction-history",
  userTokenDistributedHistory: "user-token-distibuted-history",
  userCurrentMainBalanceUsdt: "user-current-main-balance-usdt",
  launchpadStats: "launchpad-stats",
  /** `GET /v1/launchpad/get-launchpads` — public launchpad listing */
  getLaunchpads: "get-launchpads",
  /** `POST /v1/launchpad/buy-token` — body `{ launchpadId, amount }` (Bearer) */
  buyToken: "buy-token",
  /** `GET /v1/launchpad/user-balance?currencyId=&walletType=` — wallet balance (Bearer) */
  launchpadUserBalance: "user-balance",
  /** `GET /v1/launchpad/my-history` — current user's launchpad transactions (Bearer) */
  launchpadMyHistory: "my-history",




  // Options Routes

  optionPairs: "optionPairs",
  contractDates: "contractDates",
  placeOptionOrder: "order",
  cancelOptionOrder: "order",
  optionsGetOrder: "order",
  optionsOpenOrders: "open-orders",
  optionsLiveTrades: "trades",
  optionsOpenPositions: "positions",
  optionsWallet: "wallet",
  optionsPositionHistory: "positions/history",
  optionsOrderHistory: "orders/history",
  optionsTradeHistory: "trades/history",
  optionsExerciseHistory: "exercise/history",
  optionsTransactionHistory: "transaction/history",
  optionsPnlAnalysis: "pnl/analysis",
  optionsPnlAnalysisDetails: "pnl/analysis/details",
  spotPnlAnalysis: "me/pnl/analysis",
  spotPnlAnalysisDetails: "me/pnl/analysis/details",

  // Giveaway Routes
  checkGiveawayStatus: "checkEleigibleForGiveaway",
  getGiveawayList: "giveawayList",
  claimGiveaway: "claimGiveaway",


  // P2P Routes
  getCurrency: "getCurrency",
  getFiatCurrency: "getFiatCurrency",
  getAllPaymentMethods: "getAllPaymentMethods",
  p2pSellOrderForBuyer: "p2pSellOrderForBuyer",
  p2pBuyOrderForSeller: "p2pBuyOrderForSellers",
  getPaymentMethodFields: "getPaymentMethodFields",
  addUserPaymentMethod: "addUserPaymentMethod",
  getUserPaymentMethods: "getUserPaymentMethods",
  deleteUserPaymentMethod: "deletePaymentMethod",
  createAd: "createAd",
  buyFromAd: "buyFromAd",
  sellFromAd: "sellFromAd",
  getP2pOrderDetails: "getOrderDetails",
  getP2pOrders: "orders",
  getPairPrice: "getPairPrice",

  // P2P Order Actions
  markPaymentCompleted: "markPaymentCompleted",
  releaseCrypto: "releaseCrypto",
  cancelP2pOrder: "cancelOrder",
  buyerDispute: "buyerDispute",
  sellerDispute: "sellerDispute",

  // P2P Chat endpoints
  sendChatMessage: "chat/send",
  uploadChatImage: "chat/upload",
  getChatHistory: "chat",
  markChatRead: "chat/read",
  getChatUnreadCount: "chat/unread/count",
  deleteChatMessage: "chat/delete",
  getUserAds: "getUserAds",
  closeAd: "closeAd",
  updateAdStatus: "updateAdStatus",
  getP2pProfile: "profile",

  // Security & 2FA endpoints
  securityStatus: "status",
  securitySendOtp: "send-otp",
  securityVerifyOtp: "verify-otp",
  securityVerifyAllMethods: "verify-all-security-methods",
  sendOtpForEmailOrMobile: "send-otp-for-email-or-mobile",
  securityGenerateGoogleQr: "generate-google-qr",
  security2faSetup: "2fa/setup",
  security2faConfirm: "2fa/confirm",
  security2faDisable: "2fa/disable",
  securityUpdate2fa: "update-2fa",
  securityEmailAdd: "email/add",
  securityEmailChangeInitiate: "email/change/initiate",
  securityEmailChangeComplete: "email/change/complete",
  securityMobileAdd: "mobile/add",
  securityMobileChangeInitiate: "mobile/change/initiate",
  securityMobileChangeComplete: "mobile/change/complete",
  securitySettings: "settings",
  securityLogin2fa: "login-2fa",
  securityCheckTwoLogin2Step: "check-two-login-2-step",
  securitySendOtpForTwoLogin2Step: "send-otp-for-two-login-2-step",
  securityUpdateTwoLogin2Step: "update-two-login-2-step",
  securityGetLogs: "get-logs",
  securityGetLoginLogs: "get-login-logs",
  securityGetSecurityLogs: "get-security-logs",
  securityAddFundPassword: "add-fund-password",
  securityGetFundPasswordStatus: "get-fund-password-status",
  securityChangePassword: "change-password",
  /** Logged-in reset password — POST `{ method: "email" | "mobile" }` */
  securityResetPasswordSendOtp: "reset-password/send-otp",
  /** Logged-in reset password — POST `{ type: "email" | "mobile", code }` */
  securityResetPasswordVerifyOtp: "reset-password/verify-otp",
  /** Logged-in reset password — POST `{ verificationToken, newPassword, confirmNewPassword }` */
  securityResetPassword: "reset-password",
  /** Disable account — POST `{ security_methods: "email" | "mobile" | "phone" }` (Bearer). */
  securityDisableAccountSendOtp: "disable-account/send-otp",
  /** Disable account — POST `{ type, code, credential }` matches `SecurityController.disableAccount` (Bearer). */
  securityDisableAccount: "disable-account",
  /** Close account — POST `{ security_methods: "email" | "mobile" | "phone" }` (Bearer). Same shape as disable send-otp. */
  securityClosedAccountSendOtp: "closed-account/send-otp",
  /** Close account — POST `{ type, code, credential }` same contract as disable-account completion (Bearer). */
  securityClosedAccount: "closed-account",
  /** GET wallet totals for close-account assets step — `estimated_total_btc`, etc. */
  securityWalletTotalBtc: "wallet-total-btc",
  /** OAuth account connections — GET list; POST connect/disconnect after security verification */
  securityGetConnectedAccounts: "get-connected-accounts",
  /** GET — `{ success, data: number }` count of linked OAuth accounts for security hub row */
  securityGetConnectedAccountCount: "get-connected-account-count",
  /** Account connections — POST `{ action, type, code, credential?, provider? }` (`action`: connected | disconnected). */
  securityConnectDisconnectAccount: "connect-disconnect-account",
  /** POST `{ security_methods: "email" | "mobile" | "phone" }` — OTP before connect/disconnect (same shape as disable-account send-otp). */
  securityConnectDisconnectSendOtp: "send-otp-for-connect-disconnect-account",

  // Anti-phishing code endpoints
  antiPhishingStatus: "anti-phishing/status",
  antiPhishingSendOtp: "anti-phishing/send-otp",
  antiPhishingAdd: "anti-phishing/add",
  antiPhishingRemove: "anti-phishing/remove",
  /** POST `{ methods: ["email"|"mobile"|"totp"|"passkey"], freeze_acknowledged: true }` */
  resetUnavailableSecurityMethods: "reset-unavailable-security-methods",
  /** POST `{ methods, freeze_acknowledged: true }` — starts remaining-method verification session */
  startUnavailableSecurityReset: "unavailable-reset/start",

  // Passkey (WebAuthn) endpoints
  passkeyRegisterOptions: "passkey/register/options",
  passkeyRegisterVerify: "passkey/register/verify",
  passkeyList: "passkeys",
  passkeyRename: "passkey/rename",
  passkeyDelete: "passkey/delete",
  passkeyStepUpOptions: "passkey/step-up/options",
  passkeyStepUpVerify: "passkey/step-up/verify",
  passkeyAuthOptions: "passkey/auth/options",
  passkeyAuthVerify: "passkey/auth/verify",
  passkeyLoginComplete: "passkey/login/complete",
  checkTwoLogin2Step: "check-two-login-2-step",
  sendOtpForTwoLogin2Step: "send-otp-for-two-login-2-step",

  /** Emergency contacts — POST/GET relative to `baseSecurity` (`/v1/security/`) */
  emergencyContactAdd: "add-emergency-contact",
  emergencyContactEdit: "edit-emergency-contact",
  emergencyContactDelete: "delete-emergency-contact",
  emergencyContactList: "get-emergency-contact-list",
  /** GET — `{ success, data: number }` emergency contact count for security hub row */
  emergencyContactCount: "get-emergency-contact-count",
  emergencyContactSaveMessage: "save-emergency-contact-message",
  /** POST `{ security_methods: "email" | "mobile" }` — OTP for emergency-contact verification */
  emergencyContactSendOtp: "emergency-contact/send-otp",

  /** KYC (Didit) — paths relative to `baseKycApi` (`/api/v1/kyc`) */
  kycJurisdiction: "jurisdiction",
  kycSession: "session",
  /** Canonical `GET /api/v1/kyc/status` (SessionPayload) — preferred over legacy `kyc-status` for new UIs. */
  kycStatus: "status",

  /** KYB (Didit) — paths relative to `baseKybApi` (`/api/v1/kyb`) */
  kybSession: "session",
  /** Canonical `GET /api/v1/kyb/status` (SessionPayload) */
  kybStatus: "status",

  // ============URLs================

  baseUrl: `${apiOrigin}/`,
  baseKycApi: `${apiOrigin}/api/v1/kyc/`,
  baseKybApi: `${apiOrigin}/api/v1/kyb/`,
  /** KYC VARA onboarding — `/v1/user/kyc-vara/*` (backend src/kyc_vara) */
  baseKycVara: `${apiOrigin}/v1/user/kyc-vara/`,
  /** @deprecated Prefer resolvePublicUploadUrl(path) — do not concatenate for S3 keys. */
  baseImage: `${apiOrigin}/`,
  /** S3 / CDN public base (same as REACT_APP_S3_PUBLIC_BASE_URL). */
  s3PublicBaseUrl: `${s3PublicBaseUrl}/`,
  baseAuth: `${apiOrigin}/v1/user/`,
  /** Public `/v1/blogs` and `/v1/blog-categories` */
  baseBlogs: `${apiOrigin}/v1/`,
  baseSetting: `${apiOrigin}/v1/setting/`,
  baseSecurity: `${apiOrigin}/v1/security/`,
  baseUserBot: `${apiOrigin}/v1/bot/`,
  baseEarning: `${apiOrigin}/v1/earning/`,
  baseStaking: `${apiOrigin}/v1/staking/`,
  baseSoftStaking: `${apiOrigin}/v1/softstaking/`,
  softStakingPackages: "packages",
  basePartner: `${apiOrigin}/v1/partnerShip/`,
  baseTrans: `${apiOrigin}/v1/transaction/`,
  walletHistoryExport: 'wallet-history-export',
  baseExchange: `${apiOrigin}/v1/exchange/`,
  baseSpot: `${apiOrigin}/spot/v1/`,
  baseWallet: `${apiOrigin}/v1/wallet/`,
  baseMargin: `${apiOrigin}/v1/margin/`,
  marginAccounts: "accounts",
  baseCross: `${apiOrigin}/v1/cross/`,
  marginWalletTransfer: "wallet-transfer",
  marginMaxTransfer: "max-transfer",
  marginPortfolioSummary: "portfolio-summary",
  marginWalletBalances: "wallet-balances",
  /** `/api/v1/wallet/*` — withdrawal coin catalog & saved addresses (Bearer). */
  baseWalletV1Api: `${apiOrigin}/api/v1/wallet/`,
  walletWithdrawalCoins: "withdrawal-coins",
  walletWithdrawal24hUsage: "withdrawal-24h-usage",
  /** `POST /api/v1/wallet/withdrawal-request` — lock funds, wait for admin (app submit) */
  walletWithdrawalRequest: "withdrawal-request",
  /** `GET/POST /api/v1/wallet/withdrawal-declarations` and `.../:withdrawalId` */
  withdrawalDeclarations: "withdrawal-declarations",
  /** `GET /api/v1/wallet/withdrawals` — user withdrawal list (includes `awaiting_admin`) */
  walletWithdrawals: "withdrawals",
  /** `GET /api/v1/wallet/passkey-withdrawal-challenge` — Bearer */
  walletPasskeyWithdrawalChallenge: "passkey-withdrawal-challenge",
  walletAddressBook: "address-book",
  /** `GET /api/v1/wallet/withdrawal-address-history` — recent withdrawal destinations (Bearer). */
  walletWithdrawalAddressHistory: "withdrawal-address-history",
  baseCoin: `${apiOrigin}/v1/coin/`,
  /** `GET /v1/coin/get-coin` — coin list for withdraw (see withdrawService) */
  coinGetCoin: "get-coin",
  baseP2p: `${apiOrigin}/v1/p2p/`,
  baseSupport: `${apiOrigin}/v1/support/`,
  baseNotification: `${apiOrigin}/v1/notifications/`,
  baseSwap: `${apiOrigin}/v1/qbs/`,
  baseCvbot: `${apiOrigin}/v1/cvbot/`,
  baseCoinList: `${apiOrigin}/v1/coin-listing/`,
  baseFutures: `${apiOrigin}/v1/futures/`,
  baseOptions: `${apiOrigin}/v1/options/`,
  baseLaunchpad: `${apiOrigin}/v1/launchpad/`,
  /** Zand AED fiat user rail — `/v1/fiat/*` */
  baseFiatApi: `${apiOrigin}/v1/fiat/`,
  // ============webSocketUrl================

  webSocketUrl: socketUrl,
  // webSocketUrl:  "http://localhost:9510",
};
